/**
 * ButtonGuard — wraps write-action buttons to enforce RBAC at the UI level.
 *
 * Usage:
 *   <ButtonGuard requirePermission="USER_WRITE">
 *     {({ disabled, reason }) => (
 *       <button disabled={disabled} title={reason}>신규 등록</button>
 *     )}
 *   </ButtonGuard>
 *
 * This is ADDITIVE — existing server-side permission checks remain intact.
 * The guard prevents accidental clicks and provides immediate UX feedback.
 */
import type { ReactNode } from 'react';
import { useCurrentUser, useHasPermission } from '../hooks/useCurrentUser';

interface ButtonGuardProps {
  /**
   * Permission code that must be present in CurrentUser.permissions.
   * Example: 'USER_WRITE', 'ROLE_WRITE', 'MENU_WRITE', 'SETTING_WRITE'
   */
  requirePermission?: string;
  /**
   * At least one of these roles must be present in CurrentUser.roles.
   * Example: ['ROLE_ADMIN']
   */
  requireAnyRole?: string[];
  /**
   * Render prop — receives { disabled, reason } so the caller controls
   * the exact element type (button, icon-button, etc.).
   */
  children: (state: { disabled: boolean; reason?: string }) => ReactNode;
}

export function ButtonGuard({
  requirePermission,
  requireAnyRole,
  children,
}: ButtonGuardProps) {
  const user = useCurrentUser();
  const hasPerm = useHasPermission(requirePermission ?? '');
  const userRoles = user?.roles ?? [];
  const hasRole = requireAnyRole
    ? requireAnyRole.some((r) => userRoles.includes(r))
    : true;

  let disabled = false;
  let reason: string | undefined;

  if (requirePermission && !hasPerm) {
    disabled = true;
    reason = `권한이 없습니다 (현재 역할: ${userRoles[0] ?? '?'})`;
  } else if (requireAnyRole && !hasRole) {
    disabled = true;
    reason = `해당 역할만 접근 가능합니다 (필요: ${requireAnyRole.join(', ')})`;
  }

  return <>{children({ disabled, reason })}</>;
}
